// OrderList.js

import { useQuery } from '@tanstack/react-query';
import { fetchAllCourseSubscriptions } from 'api';
import { columnsDataOrders } from './variables/columnsDataOrders';
import SearchTableOrders from './components/SearchTableOrders';
import Card from 'components/card';
import DottedLoader from 'components/loaders/DottedLoader';

const OrderList = () => {
  const {
    data: subscriptions = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['courseSubscriptions'],
    queryFn: fetchAllCourseSubscriptions,
  });

  if (isLoading) {
    return <DottedLoader text="Loading orders..." />;
  }

  if (isError) {
    return <div>Error: {error.message}</div>;
  }

  // Include all subscriptions
  const allSubscriptions = subscriptions;

  const tableData = allSubscriptions.map((sub) => {
    // Safely access billing data with fallbacks
    const billingAmount = sub.billing?.amount || 0;
    const billingStatus = sub.billing?.status || 'N/A';
    const paymentMethod = sub.billing?.paymentMethod || 'N/A';
    const accountReference = sub.billing?.accountReference || 'N/A';
    const transactionId = sub.billing?.transactionId || 'N/A';
    
    // Create proper date objects for sorting
    const createdAt = new Date(sub.createdAt);
    const updatedAt = new Date(sub.updatedAt);
    
    // Format price based on payment method
    const formattedPrice = formatPrice(billingAmount, paymentMethod);
    
    const processedData = {
      id: sub.id,
      userId: sub.user?.id || 'N/A', // Add userId with fallback
      name: sub.course?.title || 'Unknown Course', // Course name with fallback
      email: sub.user?.email || 'N/A',
      phone: sub.user?.phoneNumber || "N/A",
      price: formattedPrice, // Price formatted based on payment method
      date: createdAt.toLocaleDateString(),
      dateTime: createdAt.toLocaleString(), // Full date and time for sorting
      createdAt: createdAt, // Date object for sorting
      updatedAt: updatedAt, // Date object for sorting
      status: getStatusDisplay(sub.status, billingStatus),
      paymentMethod: getPaymentMethodDisplay(paymentMethod),
      actions: 'Actions',
      // Add additional data for payment confirmation
      billingStatus: billingStatus,
      paymentMethod: paymentMethod,
      accountReference: accountReference,
      transactionId: transactionId,
      hasBilling: !!sub.billing, // Flag to check if billing exists
      // For M-Pesa manual confirmation, we don't pre-fill the reference
      // Admin needs to enter the actual M-Pesa receipt number
    };

    return processedData;
  });

  // Helper function to format price based on payment method
  function formatPrice(amount, paymentMethod) {
    switch (paymentMethod) {
      case 'WaafiPay':
        return `$${amount.toFixed(2)}`; // Dollars for WaafiPay
      case 'Mpesa':
        return `Kshs. ${amount.toFixed(2)}`; // Kenyan shillings for M-Pesa
      default:
        return `Kshs. ${amount.toFixed(2)}`; // Default to Kenyan shillings
    }
  }

  // Helper function to map subscription status to display status
  function getStatusDisplay(subscriptionStatus, billingStatus) {
    // Check if payment is in progress (pending subscription with PENDING billing)
    if (subscriptionStatus === 'pending' && billingStatus === 'PENDING') {
      return 'In Progress';
    }
    
    switch (subscriptionStatus) {
      case 'active':
        return 'Completed';
      case 'pending':
        return 'Pending';
      case 'expired':
        return 'Expired';
      case 'canceled':
        return 'Canceled';
      case 'failed':
        return 'Failed';
      default:
        return subscriptionStatus || 'Unknown';
    }
  }

  // Helper function to format payment method display
  function getPaymentMethodDisplay(paymentMethod) {
    switch (paymentMethod) {
      case 'Mpesa':
        return 'M-Pesa';
      case 'WaafiPay':
        return 'WaafiPay';
      default:
        return paymentMethod || 'N/A';
    }
  }

  return (
    <Card extra="w-full h-full bg-white mt-3">
      <SearchTableOrders
        tableData={tableData}
        columnsData={columnsDataOrders}
      />
    </Card>
  );
};

export default OrderList;
